import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { Store } from '@ngrx/store';
import { take, switchMap, tap, of, Subject, debounceTime, distinctUntilChanged } from 'rxjs';
import { ITopCompanies } from '../../../../../../core/models/results.model';
import { loadSidenavData, submitSearch } from '../../../../../../state/action';
import { AppState } from '../../../../../../state/app.state';
import { selectSidenavDataByTabIdAndKey } from '../../../../../../state/selectors';
import { GeneralInnovationActivityResultsService } from '../../../../services/general-innovation-activity-results.service';
import { CommonModule } from '@angular/common';
import { NgxPaginationModule } from 'ngx-pagination';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { REGION_CODES } from '../../../../../../../../public/country-region-codes';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import * as XLSX from 'xlsx';
import { NullableNumberPipe } from '../../../../../../shared/pipes/nullable-number.pipe';
import { MatDialog } from '@angular/material/dialog';
import { CompanyPopupComponent } from '../thematic-competition-overview/company-popup.component';
import { AddToWatchlistPopupComponent } from 'src/app/shared/components/add-to-watchlist-popup/add-to-watchlist-popup.component';
import { ConfirmationDialogComponent } from 'src/app/shared/components/confirmation-dialog/confirmation-dialog.component';

interface universeSelector {
  query: string | null | undefined,
  tabId: string,
  include_public: boolean,
  include_private: boolean,
  min_mcap?: number,
  max_mcap?: number
}

@Component({
  selector: 'app-thematic-public-competitors',
  standalone: true,
  imports: [CommonModule, NgxPaginationModule, MatTooltipModule, ReactiveFormsModule, FormsModule, MatSlideToggleModule, NullableNumberPipe],
  templateUrl: './thematic-public-competitors.component.html',
  styleUrl: './thematic-public-competitors.component.css'
})
export class ThematicPublicCompetitorsComponent implements OnInit, OnChanges {
  @Input() tabId!: string;
  @Input() query: string | null = null;
  @Input() entityids: string[] | null = null;
  @Input() tab_name: string | null = null;

  constructor(private store: Store<AppState>, private apiService: GeneralInnovationActivityResultsService, private dialog: MatDialog
  ) {

  }

  showQueryBox: boolean = false;

  // Toggle query box
  toggleQueryBox(): void {
    this.showQueryBox = !this.showQueryBox;
  }
  paginator_1_page: number = 1;
  params: any;

  // Category system
  selectedCategory: string = 'all_competitors';
  categoryTabs = [
    {
      key: 'all_competitors',
      label: 'All Competitors',
      tooltip: 'All public companies in theme, no filter, innovation score ranking.',
      description: 'All public companies in the theme, ranked by innovation score.'
    },
    {
      key: 'innovation_leaders',
      label: 'Innovation Leaders',
      tooltip: 'Innovation Leaders are companies that rank highest among competitors based on their innovation score, a composite score reflecting size, growth and quality, relative to the peer group.',
      enhancedTooltip: 'Innovation Leaders are companies that rank highest among competitors based on their innovation score, a composite score reflecting size, growth and quality, relative to the peer group.',
      description: 'Innovation Leaders are the 50 companies that stand out with a strong innovation activity balanced across portfolio size, recent growth and innovation quality.'
    },
    {
      key: 'growth_leaders',
      label: 'Growth Leaders',
      tooltip: 'Growth Leaders are companies with the highest relative growth, among companies with at least 10 inventions published in the last 12 months.',
      enhancedTooltip: 'Growth Leaders are companies with the highest relative growth, among companies with at least 10 inventions published in the last 12 months.',
      description: 'Growth Leaders are the 50 companies which show the most significant recent acceleration in their innovation activity.'
    },
    {
      key: 'size_leaders',
      label: 'Size Leaders',
      tooltip: 'Size Leaders are companies with the highest number of inventions published in the last 10 years.',
      description: 'Size Leaders are the 50 companies with the largest innovation portfolios among competitors, responsible for the largest share of the innovation in their space.'
    },
    {
      key: 'quality_leaders',
      label: 'Quality Leaders',
      tooltip: 'Quality Leaders are companies with the highest innovation quality, among companies with at least 10 inventions in the last 12m.',
      description: 'Quality Leaders are the 50 companies that prioritize quality over quantity, likely to produce the largest individual breakthroughs.'
    },
    {
      key: 'upcoming',
      label: 'Up and Coming',
      tooltip: 'Up and Coming are companies with the highest innovation score among companies with at least a third of their inventions filed in the last year.',
      description: 'Up and Coming are the 50 companies that have the strongest innovation activity among companies that have recently increased their efforts in this technology area.'
    },

  ];

  // Search functionality
  private searchSubject = new Subject<string>();
  isSearching: boolean = false;

  // Category selection method
  selectCategory(categoryKey: string): void {
    this.selectedCategory = categoryKey;
    this.paginator_1_page = 1;
    if (categoryKey !== 'all_competitors') {
      this.sortColumn = 'innovation_score';
      this.sortDirection = 'desc';
    }
    this.applyFilters();
  }

  // Get current category label
  getCurrentCategoryLabel(): string {
    const category = this.categoryTabs.find(cat => cat.key === this.selectedCategory);
    return category ? category.label : 'All Competitors';
  }

  // Get current category description
  getCurrentCategoryDescription(): string {
    const category = this.categoryTabs.find(cat => cat.key === this.selectedCategory);
    return category ? category.description : '';
  }

  // Get current category tooltip (info text)
  getCurrentCategoryTooltip(): string {
    const category = this.categoryTabs.find(cat => cat.key === this.selectedCategory);
    return category ? category.tooltip : '';
  }

  // Check if current category has results
  hasCategoryResults(): boolean {
    return this.filteredCompanyList.length > 0;
  }

  // Get category result count
  getCategoryResultCount(): number {
    return this.getBaseCategoryCount();
  }

  // Add method to handle search input changes
  onSearchInputChange(event: any): void {
    const value = event.target.value;

    // If search is completely cleared, apply filters immediately
    if (!value || value.trim() === '') {
      this.searchQuery = '';
      this.isSearching = false;
      this.applyFilters();
      return;
    }

    this.isSearching = true;
    this.searchSubject.next(value);
  }

  // Add method to clear search
  clearSearch(): void {
    this.searchQuery = '';
    this.isSearching = false;
    this.applyFilters();
  }

  roles: string[] = [];
  ngOnInit(): void {
    this.roles = JSON.parse(localStorage.getItem('roles')!);

    // Set up debounced search
    this.searchSubject.pipe(
      debounceTime(150),
      distinctUntilChanged()
    ).subscribe(query => {
      this.searchQuery = query;
      this.applyFilters();
    });
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['tabId']) {
      this.top_companies_loading_failed = false;
      this.top_companies_data = [];
      this.topCompanies();

    }
  }


  top_companies_loading_failed: boolean = false;
  top_companies_data: ITopCompanies[] = [];
  filteredCompanyList: ITopCompanies[] = [];
  showOnlyFavorites: boolean = false;
  searchQuery: string = '';
  countriesControl = new FormControl<string[]>([]);
  expandedRegions: Set<string> = new Set();

  countryFilterOptions = REGION_CODES;

  // Sorting state
  sortColumn: string = 'innovation_score';
  sortDirection: 'asc' | 'desc' = 'desc';

  // Selection state
  selectedCompanies: Set<string> = new Set();

  universeValues: universeSelector = {
    query: this.query,
    tabId: this.tabId,
    include_public: true,  // Fixed to public only
    include_private: false, // Fixed to public only
  };
  showDataMissing: boolean = false;
  showSpinner: boolean = false;
  topCompanies(forceReload: boolean = false) {
    this.showDataMissing = false;
    this.showSpinner = true;
    this.top_companies_loading_failed = false;
    this.top_companies_data = [];
    this.universeValues.query = this.query;
    this.universeValues.tabId = this.tabId;
    this.store.select(selectSidenavDataByTabIdAndKey(this.tabId, 'thematic_public_competitors')).pipe(
      take(1),
      switchMap((data) => {
        if (!data || forceReload) {
          return this.apiService.getTopCompanies(this.universeValues).pipe(
            tap((res) => {
              this.showSpinner = false;

              if (res.status === 204 || !res.body?.payload?.results?.length) {
                this.showDataMissing = true;
                return;
              }
              const payload = res.body.payload;
              if (payload.tabId !== this.tabId) {
                this.store.dispatch(loadSidenavData({
                  tabId: payload.tabId,
                  sidenavKey: 'thematic_public_competitors',
                  data: payload.results
                }));
                return;
              }
              if (payload != null) {
                this.store.dispatch(loadSidenavData({
                  tabId: this.tabId,
                  sidenavKey: 'thematic_public_competitors',
                  data: payload.results
                }));
                this.top_companies_data = payload.results;
                this.processData(this.top_companies_data);
              } else {
                this.showDataMissing = true;
              }

            })
          );
        } else {
          this.showSpinner = false;

          this.top_companies_data = data;
          this.processData(this.top_companies_data);

          return of();
        }
      })
    ).subscribe({
      next: () => {
        // console.log('Public companies data processing complete.');
      },
      error: (err) => {
        // console.error('Error fetching public companies:', err);
        this.top_companies_loading_failed = true;
        this.showSpinner = false;

      }
    });
  }

  processData(data: ITopCompanies[]): void {
    this.top_companies_data = data.map((company) => ({
      ...company,
      favorite: company.favorite ?? false,
    }));
    this.filteredCompanyList = [...this.top_companies_data];
    this.applyFilters();
  }



  toggleExpand(region: string): void {
    if (this.expandedRegions.has(region)) {
      this.expandedRegions.delete(region);
    } else {
      this.expandedRegions.add(region);
    }
  }

  isExpanded(region: string): boolean {
    return this.expandedRegions.has(region);
  }

  toggleRegionSelection(region: any): void {
    const allSelected = this.isRegionFullySelected(region);
    const regionCountryCodes = region.countries.map((c: any) => c.code);

    if (allSelected) {
      const updatedSelection = (this.countriesControl.value ?? []).filter(
        (value: string) => !regionCountryCodes.includes(value)
      );
      this.countriesControl.setValue(updatedSelection);
    } else {
      const updatedSelection = Array.from(
        new Set([...(this.countriesControl.value ?? []), ...regionCountryCodes])
      );
      this.countriesControl.setValue(updatedSelection);
    }
    this.applyFilters();
  }

  toggleCountrySelection(countryCode: string, region: any): void {
    const currentSelection = this.countriesControl.value ?? [];
    const isSelected = currentSelection.includes(countryCode);

    if (isSelected) {
      this.countriesControl.setValue(
        currentSelection.filter((value: string) => value !== countryCode)
      );
    } else {
      this.countriesControl.setValue([...currentSelection, countryCode]);
    }
    this.applyFilters();
  }

  isRegionFullySelected(region: any): boolean {
    const regionCountryCodes = region.countries.map((c: any) => c.code);
    const selectedCodes = this.countriesControl.value ?? [];
    return regionCountryCodes.every((code: string) => selectedCodes.includes(code));
  }
  filterFavorites(): void {
    this.showOnlyFavorites = !this.showOnlyFavorites;
    this.applyFilters();
  }


  applyFilters(): void {
    this.filteredCompanyList = this.top_companies_data;

    // Apply category filter first
    this.filteredCompanyList = this.applyCategoryFilter(this.filteredCompanyList);

    // Apply category ranking BEFORE filtering by selection
    // This ensures ranks are set and won't be recalculated when showing selection only
    this.applyCategoryRanking(this.filteredCompanyList);

    if (this.countriesControl.value?.length) {
      this.filteredCompanyList = this.filteredCompanyList.filter((company) =>
        this.countriesControl.value!.includes(company.country)
      );
    }

    if (this.showOnlyFavorites) {
      // Show selected companies (starred/selected via star icon)
      // Ranks are already set above, so they won't be recalculated
      this.filteredCompanyList = this.filteredCompanyList.filter((company) =>
        this.selectedCompanies.has(company.entityid) || company.favorite
      );
      this.paginator_1_page = 1;
      // Skip sorting when showing selection only to preserve ranks
      return;
    }

    if (this.searchQuery) {
      this.filteredCompanyList = this.filteredCompanyList.filter((company) =>
        company.entity_name.toLowerCase().includes(this.searchQuery.toLowerCase())
      );
    }

    // Apply sorting only for 'all_competitors' category
    if (this.selectedCategory === 'all_competitors' && this.sortColumn) {
      this.applySorting();
    }
  }

  // Toggle sorting for a column
  sort(column: string): void {
    // Only allow sorting in 'all_competitors' category
    if (this.selectedCategory !== 'all_competitors') {
      return;
    }
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'desc';
    }
    this.applySorting();
  }

  applySorting(): void {
    if (!this.sortColumn) return;
    this.paginator_1_page = 1;
    this.filteredCompanyList.sort((a, b) => {
      let aVal: any = 0, bVal: any = 0;

      switch (this.sortColumn) {
        case 'rank':
          aVal = a.rank || 0;
          bVal = b.rank || 0;
          break;
        case 'size':
          aVal = a.in_theme_invention_count_120m || 0;
          bVal = b.in_theme_invention_count_120m || 0;
          break;
        case 'growth':
          aVal = a.relative_growth_12m || 0;
          bVal = b.relative_growth_12m || 0;
          break;
        case 'quality':
          aVal = a.quality_average_12m || 0;
          bVal = b.quality_average_12m || 0;
          break;
        case 'total_size':
          aVal = a.n_inventions || 0;
          bVal = b.n_inventions || 0;
          break;
        case 'innovation_score':
          aVal = a.innovation_score || 0;
          bVal = b.innovation_score || 0;
          break;
        case 'company':
          aVal = a.entity_name || '';
          bVal = b.entity_name || '';
          break;
        case 'country':
          aVal = a.country || '';
          bVal = b.country || '';
          break;
        default:
          break;
      }

      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return this.sortDirection === 'asc'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }
      return this.sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    });
  }

  getSortIcon(column: string): string {
    if (this.selectedCategory !== 'all_competitors') return 'bi-arrow-down-up';
    if (this.sortColumn !== column) return 'bi-arrow-down-up';
    return this.sortDirection === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down';
  }

  private applyCategoryFilter(companies: ITopCompanies[]): ITopCompanies[] {
    if (!companies || companies.length === 0) return [];
    switch (this.selectedCategory) {
      case 'innovation_leaders':
        return companies
          .filter(company => (company.innovation_score) > 0)
          .sort((a, b) => (b.innovation_score) - (a.innovation_score))
          .slice(0, 50);
      case 'growth_leaders':
        // Top 50 by growth with minimum 10 inventions in last year and relative growth > 0
        return companies
          .filter(company => ((company.invention_count_12m >= 10) && (company.relative_growth_12m > 0)))
          .sort((a, b) => (b.relative_growth_12m) - (a.relative_growth_12m))
          .slice(0, 50);

      case 'size_leaders':
        return companies
          .filter(company => (company.in_theme_invention_count_120m) > 0)
          .sort((a, b) => (b.in_theme_invention_count_120m ?? 0) - (a.in_theme_invention_count_120m ?? 0))
          .slice(0, 50);

      case 'quality_leaders':
        return companies
          .filter(company => ((company.invention_count_12m >= 10) && (company.quality_average_12m > 69)))
          .sort((a, b) => (b.quality_average_12m) - (a.quality_average_12m))
          .slice(0, 50);

      case 'upcoming':
        return companies
          .filter(company => {
            const inv1y = company.invention_count_12m ;
            const inv10y = company.in_theme_invention_count_120m ;
            const growth = company.relative_growth_12m ;
            return inv1y >= 10 && growth > 0 && (inv10y > 0 ? (inv1y / inv10y) >= 0.33 : false);
          })
          .sort((a, b) => (b.innovation_score ) - (a.innovation_score ))
          .slice(0, 50);

      case 'all_competitors':
      default:
        // All companies, ranked by innovation score
        return companies.sort((a, b) => (b.innovation_score) - (a.innovation_score));
    }
  }

  // Apply category-specific ranking
  private applyCategoryRanking(companies: ITopCompanies[]): void {
    switch (this.selectedCategory) {
      case 'innovation_leaders':
        // Sort by innovation score and rank
        companies.sort((a, b) => (b.innovation_score ?? 0) - (a.innovation_score ?? 0));
        companies.forEach((c, i) => c.rank = i + 1);
        break;

      case 'growth_leaders':
        // Already sorted by growth in applyCategoryFilter, just assign ranks
        companies.forEach((c, i) => c.rank = i + 1);
        break;

      case 'size_leaders':
        // Already sorted by size in applyCategoryFilter, just assign ranks
        companies.forEach((c, i) => c.rank = i + 1);
        break;

      case 'quality_leaders':
        // Already sorted by quality in applyCategoryFilter, just assign ranks
        companies.forEach((c, i) => c.rank = i + 1);
        break;

      case 'upcoming':
        // Already sorted by innovation score in applyCategoryFilter, just assign ranks
        companies.forEach((c, i) => c.rank = i + 1);
        break;

      case 'all_competitors':
      default:
        // Sort by innovation score and rank
        companies.sort((a, b) => (b.innovation_score ?? 0) - (a.innovation_score ?? 0));
        companies.forEach((c, i) => c.rank = i + 1);
        break;
    }
  }

  // Get base category count (before country filtering)
  private getBaseCategoryCount(): number {
    // Apply category filter to base list without country filtering
    const baseFiltered = this.applyCategoryFilter([...this.top_companies_data]);
    return baseFiltered.length;
  }


  // Reset category to default
  resetCategory(): void {
    this.selectedCategory = 'all_competitors';
    this.paginator_1_page = 1;
  }

  // Reset component state
  resetState(): void {
    this.selectedCategory = 'all_competitors';
    this.paginator_1_page = 1;
    this.searchQuery = '';
    this.isSearching = false;
    this.showOnlyFavorites = false;
    this.countriesControl.setValue([]);
  }

  newSearch(company: ITopCompanies) {
    const searchParams = { 'companies': [company] };

    this.store.dispatch(submitSearch({ searchParams, searchType: 'company' }));
  }

  getBarColor(score: number): string {
    if (score >= 90) return 'green';
    if (score >= 50) return 'orange';
    return 'red';
  }
  // Method to download the Excel file

  downloadExcel(): void {
    const filteredList = this.top_companies_data.map(item => ({
      rank: item.rank,
      company: item.entity_name || 'Unknown Company',
      isin: item.isin,
      country: item.country,
      in_theme_size_10y:item.in_theme_invention_count_120m ? Math.round(item.in_theme_invention_count_120m) : item.in_theme_invention_count_120m,
      absolute_growth_12m: item.invention_count_12m ? Math.round(item.invention_count_12m) : item.invention_count_12m,
      relative_growth_12m: item.relative_growth_12m ? Math.round(item.relative_growth_12m * 10) / 10 : item.relative_growth_12m,
      total_size:item.n_inventions ? Math.round(item.n_inventions) : item.n_inventions,
      quality_score: item.quality_average_12m ? Math.round(item.quality_average_12m * 10) / 10 : item.quality_average_12m,
      innovation_score: item.innovation_score ? Math.round(item.innovation_score * 10) / 10 : item.innovation_score,
    }));

    const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(filteredList);

    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    ws['!cols'] = [
      { wch: 10 }, { wch: 20 }, { wch: 10 }, { wch: 10 }, { wch: 15 },
      { wch: 20 }, { wch: 20 }, { wch: 20 }, { wch: 20 }, { wch: 20 }
    ];
    const sheetName = "public_competitors";
    XLSX.utils.book_append_sheet(wb, ws, sheetName);

    // if the tab name is too big we shorten it to 30 characters
    const tabName = this.tab_name ? this.tab_name.substring(0, 30) : 'public_competitors';
    const fileName = this.tab_name ? `${tabName}_public_competitors.xlsx` : 'public_competitors.xlsx';
    XLSX.writeFile(wb, fileName);
  }


  openCompanyPopup(company: ITopCompanies) {
    const dialogRef = this.dialog.open(CompanyPopupComponent, {
      width: '90vw',
      height: '85vh',
      maxWidth: '1200px',
      maxHeight: '800px',
      position: { top: '80px' }, // Position below top navigation bar
      data: {
        company: company,
        theme: this.tab_name,
        tabId: this.tabId,
        query: this.query
      },
      // disableClose: true,
    });

    dialogRef.afterClosed().subscribe(result => {
      // Handle any cleanup if needed
    });
  }

  // Check if company is public
  isPublicCompany(company: ITopCompanies): boolean {
    return company.companytype === 'Public Company' || company.companytype === 'Public Investment Firm';
  }

  // Check if company has GICS assignment
  hasGICS(company: ITopCompanies): boolean {
    const comp = company as any;
    return !!(comp.gsubind || comp.gsubind_extended || comp.entity_gics || comp.subindustryname);
  }

  // Check if company is eligible for watchlist (public + GICS)
  isEligibleForWatchlist(company: ITopCompanies): boolean {
    return this.isPublicCompany(company) && this.hasGICS(company);
  }

  // Toggle company selection (works for all companies, not just eligible)
  toggleCompanySelection(company: ITopCompanies): void {
    if (this.selectedCompanies.has(company.entityid)) {
      this.selectedCompanies.delete(company.entityid);
    } else {
      this.selectedCompanies.add(company.entityid);
    }
  }

  // Check if company is selected
  isCompanySelected(entityid: string): boolean {
    return this.selectedCompanies.has(entityid);
  }

  // Get selected eligible companies
  getSelectedEligibleCompanies(): ITopCompanies[] {
    return this.filteredCompanyList.filter(c =>
      this.selectedCompanies.has(c.entityid) && this.isEligibleForWatchlist(c)
    );
  }

  // Open Add to Watchlist popup for selected companies
  openAddToWatchlist(): void {
    const selected = this.filteredCompanyList.filter(c => this.selectedCompanies.has(c.entityid));
    const eligible = selected.filter(c => this.isEligibleForWatchlist(c));
    const ineligible = selected.filter(c => !this.isEligibleForWatchlist(c));

    if (ineligible.length > 0) {
      const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
        width: '450px',
        data: {
          title: 'Add to Watchlist',
          message: `Out of ${selected.length} selected companies, ${eligible.length} public companies with valid GICS will be added to the watchlist. ${ineligible.length} companies cannot be added.`,
          confirmText: 'Continue',
          cancelText: 'Cancel',
          confirmColor: 'primary'
        }
      });

      dialogRef.afterClosed().subscribe((confirmed) => {
        if (confirmed && eligible.length > 0) {
          const watchlistDialog = this.dialog.open(AddToWatchlistPopupComponent, {
            width: '600px',
            data: {
              companies: eligible.map(c => ({
                entityid: c.entityid,
                entity_name: c.entity_name,
                entity_gics: (c as any).gsubind_extended || ((c as any).gsubind ? (Array.isArray((c as any).gsubind) ? (c as any).gsubind[0] : (c as any).gsubind) : undefined),
                isin: c.isin,
                companytype: c.companytype,
                gsubind: (c as any).gsubind,
                gsubind_extended: (c as any).gsubind_extended
              })),
              tabId: this.tabId
            }
          });

          watchlistDialog.afterClosed().subscribe(() => {
            // Only clear selection when user actually added (handled elsewhere if needed); do not clear on cancel
          });
        }
      });
    } else if (eligible.length > 0) {
      const dialogRef = this.dialog.open(AddToWatchlistPopupComponent, {
        width: '600px',
        data: {
          companies: eligible.map(c => ({
            entityid: c.entityid,
            entity_name: c.entity_name,
            entity_gics: (c as any).gsubind_extended || ((c as any).gsubind ? (Array.isArray((c as any).gsubind) ? (c as any).gsubind[0] : (c as any).gsubind) : undefined),
            isin: c.isin,
            companytype: c.companytype,
            gsubind: (c as any).gsubind,
            gsubind_extended: (c as any).gsubind_extended
          })),
          tabId: this.tabId
        }
      });

      dialogRef.afterClosed().subscribe(() => {
        // Do not clear selection on dialog close so cancel keeps the stars selected
      });
    }
  }

}
